import { Box, Dialog, DialogTitle, useMediaQuery, useTheme } from '@mui/material'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { FormProvider } from 'react-hook-form'
import { EditModeProvider } from 'contexts/edit-mode.context'
import ActionButtons from '../../../components/action-buttons/action-buttons.component'
import { useUpdateDriverForm } from '../../../components/forms/update-driver-form/update-driver-form.hooks'
import UpdateDriverForm from '../../../components/forms/update-driver-form/update-driver-form.component'
import {
  useListDriversQuery,
  useUpdateDriverMutation,
} from '../../../store/drivers/drivers.api'
import { Driver, UpdateDriverPayload } from '../../../store/drivers/drivers.type'
import { setSelectedDriver } from '../../../store/drivers/drivers.slice'
import { extractDate } from '../../../utils/date-time'
import { getUserId } from 'utils/common'

type Props = {
  open: boolean
  handleClose: () => void
  driver: Driver
}

const emptyFormValues = () => ({
  firstName: '',
  lastName: '',
  address: '',
  licenseNumber: '',
  dateOfIssue: '',
  expiryDate: '',
  driverReferenceNumber: '',
  sex: '',
  licenseClass: '',
  restrictions: '',
  dateOfBirth: '',
  height: '',
  typeOfLicense: '',
  citizenshipCountry: '',
  fastCardNumber: '',
})

const toDateInputValue = (value?: string | null) => {
  if (!value) return ''
  return extractDate(value)
}

const formValuesFromDriver = (driver: Driver) => ({
  firstName: driver.firstName || '',
  lastName: driver.lastName || '',
  address: driver.address || '',
  licenseNumber: driver.licenseNumber || '',
  dateOfIssue: toDateInputValue(driver.dateOfIssue),
  expiryDate: toDateInputValue(driver.expiryDate),
  driverReferenceNumber: driver.driverReferenceNumber || '',
  sex: driver.sex || '',
  licenseClass: driver.licenseClass || '',
  restrictions: driver.restrictions || '',
  dateOfBirth: toDateInputValue(driver.dateOfBirth),
  height: driver.height ? String(driver.height) : '',
  typeOfLicense: driver.typeOfLicense || '',
  citizenshipCountry: driver.citizenshipCountry || '',
  fastCardNumber: driver.fastCardNumber || '',
})

const toPayload = (data: UpdateDriverPayload): UpdateDriverPayload => ({
  firstName: data.firstName,
  lastName: data.lastName,
  address: data.address || '',
  licenseNumber: data.licenseNumber || '',
  dateOfIssue: data.dateOfIssue || null,
  expiryDate: data.expiryDate || null,
  driverReferenceNumber: data.driverReferenceNumber || '',
  sex: data.sex || '',
  licenseClass: data.licenseClass || '',
  restrictions: data.restrictions || '',
  dateOfBirth: data.dateOfBirth || null,
  height: data.height || '',
  typeOfLicense: data.typeOfLicense || '',
  citizenshipCountry: data.citizenshipCountry || '',
  fastCardNumber: data.fastCardNumber || '',
})

const DriverUpdateDialog = ({ open, handleClose, driver }: Props) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { form: formMethods } = useUpdateDriverForm()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const userId = getUserId()

  const [updateDriver] = useUpdateDriverMutation()
  const { refetch } = useListDriversQuery({ userId }, { skip: !userId })

  useEffect(() => {
    if (open) {
      formMethods.reset(formValuesFromDriver(driver))
    }
  }, [open, driver, formMethods])

  const handleCancel = () => {
    handleClose()
    formMethods.reset(emptyFormValues())
  }

  const handleSave = async (data: UpdateDriverPayload) => {
    try {
      const saved = await updateDriver({
        driverId: driver.id,
        ...toPayload(data),
      }).unwrap()

      refetch()
      dispatch(setSelectedDriver(saved))
      formMethods.reset(emptyFormValues())
      handleClose()
    } catch {
      // Keep the dialog open so the user can retry.
    }
  }

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <Box>
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(handleSave)}>
            <DialogTitle sx={{ fontWeight: 700, pb: 0, px: 4, pt: 3 }}>
              Update Driver
            </DialogTitle>
            <Box p={4} pt={2} sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <EditModeProvider initialEditMode suppressAutofill>
                <UpdateDriverForm key={`edit-${driver.id}`} />
              </EditModeProvider>
            </Box>
            <ActionButtons
              firstBtnProps={{
                title: 'Save',
                onClick: formMethods.handleSubmit(handleSave),
              }}
              secondBtnProps={{
                title: 'Cancel',
                onClick: handleCancel,
                bgcolor: 'secondary.main',
              }}
            />
          </form>
        </FormProvider>
      </Box>
    </Dialog>
  )
}

export default DriverUpdateDialog
